import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ProductoTierPriceApiService } from '../services/producto-tier-price-api.service';
import {
  ProductTierPriceRequest,
  ProductTierPriceResponse,
  TierPriceCalculateResponse,
} from '../models/producto-tier-price.model';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';
import { PRICING_METHOD_LABELS, productoLabel } from '../models/producto-labels';

@Component({
  selector: 'app-producto-tier-prices',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './producto-tier-prices.component.html',
})
export class ProductoTierPricesComponent implements OnInit {
  @Input() productId!: number;
  @Input() businessId!: number;
  @Input() currency?: string;

  tiers: ProductTierPriceResponse[] = [];
  loading = false;
  saving = false;
  error = '';
  success = '';

  // Global pricing method for all tiers
  globalPricingMethod = 'volume';

  // Editable tiers (used for form)
  editTiers: ProductTierPriceRequest[] = [];

  // Calculator
  calcQuantity: number | null = null;
  calcResult: TierPriceCalculateResponse | null = null;
  calculating = false;
  calcError = '';
  private calcDebounce?: ReturnType<typeof setTimeout>;

  constructor(
    private productApi: ProductoTierPriceApiService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService,
  ) {}

  ngOnInit(): void {
    this.cargarTiers();
  }

  cargarTiers(): void {
    this.loading = true;
    this.productApi.getProductTierPrices(this.productId).subscribe({
      next: (data) => {
        this.tiers = data;
        this.editTiers = data.map((t) => ({
          pricingMethod: t.pricingMethod,
          tierLevel: t.tierLevel,
          minUnits: t.minUnits,
          maxUnits: t.maxUnits,
          unitPrice: t.unitPrice,
          fixedPrice: t.fixedPrice,
        }));
        this.globalPricingMethod = data[0]?.pricingMethod || 'volume';
        this.loading = false;
      },
      error: () => {
        this.tiers = [];
        this.editTiers = [];
        this.loading = false;
      },
    });
  }

  agregarTramo(): void {
    const nextLevel = this.editTiers.length + 1;
    this.editTiers.push({
      pricingMethod: this.globalPricingMethod,
      tierLevel: nextLevel,
      minUnits: 1,
      maxUnits: undefined,
      unitPrice: 0,
      fixedPrice: undefined,
    });
  }

  quitarTramo(index: number): void {
    this.editTiers.splice(index, 1);
    // Renumber tier levels
    this.editTiers.forEach((t, i) => (t.tierLevel = i + 1));
  }

  onPricingMethodChange(): void {
    this.editTiers.forEach((t) => (t.pricingMethod = this.globalPricingMethod));
  }

  guardarTramos(): void {
    if (this.editTiers.length === 0) return;
    this.saving = true;
    this.error = '';
    this.success = '';

    // Apply global method to all
    const payload = this.editTiers.map((t) => ({
      ...t,
      pricingMethod: this.globalPricingMethod,
    }));

    this.productApi
      .updateProductTierPrices(this.productId, this.businessId, payload)
      .subscribe({
        next: (data) => {
          this.tiers = data;
          this.saving = false;
          this.success = 'Tramos guardados correctamente.';
          setTimeout(() => (this.success = ''), 3000);
          this.toast.updated('Tramos de precio');
        },
        error: (err) => {
          this.saving = false;
          this.error = err.error?.message || 'Error al guardar los tramos';
          this.toast.error({
            title: 'Error',
            message: err.error?.message || 'Error al guardar los tramos',
          });
        },
      });
  }

  async eliminarTodos(): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar tramos',
      message:
        '¿Eliminar todos los tramos de precio? Esta acción no se puede deshacer.',
    });
    if (!ok) return;
    this.productApi
      .deleteProductTierPrices(this.productId, this.businessId)
      .subscribe({
        next: () => {
          this.tiers = [];
          this.editTiers = [];
          this.toast.deleted('Tramos de precio');
        },
        error: (err) => {
          this.error = err.error?.message || 'Error al eliminar los tramos';
          this.toast.error({
            title: 'Error',
            message: err.error?.message || 'Error al eliminar los tramos',
          });
        },
      });
  }

  /** Inc 36: recalcula en tiempo real (con pequeño debounce) al cambiar la cantidad. */
  onCalcQuantityChange(): void {
    this.calcResult = null;
    this.calcError = '';
    if (this.calcDebounce) clearTimeout(this.calcDebounce);
    if (this.calcQuantity === null || this.calcQuantity === undefined) return;
    this.calcDebounce = setTimeout(() => this.calcularPrecio(), 400);
  }

  calcularPrecio(): void {
    this.calcError = '';
    if (
      this.calcQuantity === null ||
      this.calcQuantity === undefined ||
      this.calcQuantity <= 0
    ) {
      this.calcResult = null;
      this.calcError = 'Ingresa una cantidad válida (mayor que cero).';
      return;
    }
    if (!this.productId) {
      this.calcError =
        'Guarda el producto antes de calcular precios por tramo.';
      return;
    }
    this.calculating = true;
    this.calcResult = null;
    this.productApi
      .calculateProductTierPrice(this.productId, this.calcQuantity)
      .subscribe({
        next: (result) => {
          this.calcResult = result;
          this.calculating = false;
        },
        error: (err) => {
          this.calculating = false;
          this.calcResult = null;
          this.calcError =
            err.error?.message ||
            'No se pudo calcular el precio para esa cantidad.';
        },
      });
  }

  pricingMethodLabel(value: string | null | undefined): string {
    return productoLabel(PRICING_METHOD_LABELS, value);
  }

  formatPrecio(price?: number): string {
    if (price === undefined || price === null) return '-';
    return `${this.currency} ${price}`;
  }
}
